import { RemoteFrameRefreshPayload } from "./types";

/**
 * Merges query tuples by key, with incoming values overriding existing entries.
 *
 * @param base - Existing query tuple list.
 * @param incoming - New query tuple list to apply.
 * @returns Merged query tuple list.
 */
export function mergeQueryEntries(
  base: Array<[string, string | null]>,
  incoming: Array<[string, string | null]>
): Array<[string, string | null]> {
  if (!incoming.length) return base;

  const map = new Map<string, string | null>();
  for (let i = 0; i < base.length; i++) {
    map.set(base[i][0], base[i][1]);
  }

  for (let i = 0; i < incoming.length; i++) {
    map.set(incoming[i][0], incoming[i][1]);
  }

  return Array.from(map.entries());
}

/**
 * Parses a query string into key/value tuples.
 *
 * @param query - Raw query string with or without leading `?`.
 * @returns Parsed tuple entries.
 */
export function parseQueryString(query: string): Array<[string, string | null]> {
  const normalized = query.startsWith("?") ? query.slice(1) : query;
  if (!normalized) return [];

  const params = new URLSearchParams(normalized);
  const entries: Array<[string, string | null]> = [];
  params.forEach((value, key) => {
    entries.push([key, value]);
  });
  return entries;
}

/**
 * Normalizes payload query input into tuple entries.
 *
 * @param query - Query payload value.
 * @returns Tuple entries, or `null` when no query input was provided.
 */
export function parseQueryInput(
  query: RemoteFrameRefreshPayload["query"]
): Array<[string, string | null]> | null {
  if (typeof query === "undefined") return null;
  if (typeof query === "string") {
    return parseQueryString(query);
  }

  return objectToQueryEntries(query);
}

/**
 * Converts a query object to tuple entries.
 *
 * @param query - Query object payload.
 * @returns Tuple entries preserving `null` and skipping `undefined`.
 */
export function objectToQueryEntries(
  query: Record<string, string | number | boolean | null | undefined>
): Array<[string, string | null]> {
  const entries: Array<[string, string | null]> = [];

  for (const key of Object.keys(query)) {
    const value = query[key];
    if (value === undefined) continue;
    if (value === null) {
      entries.push([key, null]);
      continue;
    }
    entries.push([key, String(value)]);
  }

  return entries;
}

/**
 * Serializes query tuple entries into a URL query string.
 *
 * @param entries - Query tuple entries.
 * @returns Query string without leading `?`.
 */
export function queryEntriesToString(entries: Array<[string, string | null]>): string {
  const params = new URLSearchParams();
  for (let i = 0; i < entries.length; i++) {
    const [key, value] = entries[i];
    if (value === null) continue;
    params.set(key, value);
  }
  return params.toString();
}
